import { ChevronDown, ChevronRight } from "@procore/core-icons";
import { Box, Button, colors, Link, Modal, spacing, Typography } from "@procore/core-react";
import { type MouseEvent, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { getBrowserDisplayName } from "../browserDisplayName";
import { type GuidanceItemKey, type GuidanceSnapshot } from "../guidanceModel";
import { trackLinkClicked, type WizardLinkItem } from "../telemetry";
import { openDefaultGraphicsSettings, openGraphicsSettings } from "../windowsSettingsLinks";
import { trapFocusWithoutDismiss } from "./guidanceModalLock";
import { GuidanceVideo } from "./GuidanceVideo";

type StepsChecklistProps = {
    snapshot: GuidanceSnapshot;
    busy?: boolean;
    initialExpandedKey?: GuidanceItemKey;
    onIgnore: () => void;
};

/** Same performance troubleshooting article the weak-device snackbar uses. */
export const GPU_GUIDANCE_LEARN_MORE_URL =
    "https://v2.support.procore.com/product-manuals/model-manager-project/optimize-your-web-browser-for-model-manager";

/** Figma: BIM — Windows GPU Wizard, steps dialog 672×752. */
const STEPS_DIALOG_WIDTH = 672;
const HEADER_HEIGHT = 60;
const HEADER_PAD_X = 30;
const FIGMA_BORDER = "#D6DADC";
const FIGMA_LINK = "#1D5CC9";

export function StepsChecklist({ snapshot, busy = false, initialExpandedKey, onIgnore }: StepsChecklistProps) {
    const { t } = useTranslation();
    const titleId = useId();
    const bestPracticesId = useId();
    const [bestPracticesExpanded, setBestPracticesExpanded] = useState(Boolean(initialExpandedKey));
    const browser = getBrowserDisplayName();

    return (
        <Modal
            open
            role="dialog"
            aria-labelledby={titleId}
            howToClose={[]}
            onClose={busy ? trapFocusWithoutDismiss : onIgnore}
            style={{
                width: STEPS_DIALOG_WIDTH,
                maxWidth: STEPS_DIALOG_WIDTH,
                borderRadius: 8,
                boxShadow: "0 0 28px rgba(0, 0, 0, 0.28)",
                overflow: "hidden",
            }}
        >
            <Modal.Header
                style={{
                    boxSizing: "border-box",
                    height: HEADER_HEIGHT,
                    padding: `0 ${HEADER_PAD_X}px`,
                    borderBottom: `1px solid ${FIGMA_BORDER}`,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Modal.Heading id={titleId} style={{ fontWeight: "bold" }}>
                    {t("gpuGuidance.steps.title")}
                </Modal.Heading>
            </Modal.Header>
            <Modal.Body
                data-testid="gpu-guidance-steps"
                style={{
                    padding: `${spacing.xl}px ${spacing.xl}px 0`,
                    maxWidth: "none",
                }}
            >
                <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
                    {snapshot.isWindows ? <GuidanceVideo /> : null}
                    <Box
                        as="ol"
                        display="flex"
                        flexDirection="column"
                        style={{
                            margin: 0,
                            padding: "26px 30px",
                            listStyle: "none",
                            border: `1px solid ${FIGMA_BORDER}`,
                            borderRadius: 8,
                            gap: 22,
                        }}
                    >
                        <StepRow n={1}>
                            <Typography intent="body" color="gray15">
                                {t("gpuGuidance.steps.acceleration.enable")}{" "}
                                <Typography as="span" intent="body" weight="semibold" color="gray15">
                                    {t("gpuGuidance.steps.acceleration.emphasis")}
                                </Typography>{" "}
                                {t("gpuGuidance.steps.acceleration.in")}{" "}
                                {t("gpuGuidance.steps.acceleration.browserSettings", { browser })}
                            </Typography>
                        </StepRow>
                        <StepRow n={2}>
                            <Typography intent="body" color="gray15">
                                {t("gpuGuidance.steps.gpuPreference.select")}{" "}
                                <Typography as="span" intent="body" weight="semibold" color="gray15">
                                    {t("gpuGuidance.steps.gpuPreference.emphasis")}
                                </Typography>{" "}
                                {t("gpuGuidance.steps.gpuPreference.rest", { browser })}{" "}
                                {snapshot.isWindows ? (
                                    <StepLink
                                        href="#"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            trackLinkClicked("gpu-preference");
                                            openGraphicsSettings();
                                        }}
                                    >
                                        {t("gpuGuidance.steps.gpuPreference.windowsSettings")}
                                    </StepLink>
                                ) : (
                                    t("gpuGuidance.steps.gpuPreference.windowsSettings")
                                )}
                            </Typography>
                        </StepRow>
                        <StepRow n={3}>
                            <Typography intent="body" color="gray15">
                                {t("gpuGuidance.steps.restart")}
                            </Typography>
                        </StepRow>
                        <StepRow n={4}>
                            <button
                                type="button"
                                aria-expanded={bestPracticesExpanded}
                                aria-controls={bestPracticesId}
                                onClick={() =>
                                    setBestPracticesExpanded((open) => {
                                        if (!open) {
                                            trackLinkClicked("best-practices");
                                        }
                                        return !open;
                                    })
                                }
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    width: "100%",
                                    gap: spacing.sm,
                                    padding: 0,
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    color: "inherit",
                                }}
                            >
                                <Typography intent="body" color="gray15" style={{ flex: 1 }}>
                                    {t("gpuGuidance.steps.bestPractices")}
                                </Typography>
                                {bestPracticesExpanded ? (
                                    <ChevronDown
                                        size="sm"
                                        aria-hidden="true"
                                        style={{ color: colors.gray15, flexShrink: 0, marginTop: 2 }}
                                    />
                                ) : (
                                    <ChevronRight
                                        size="sm"
                                        aria-hidden="true"
                                        style={{ color: colors.gray15, flexShrink: 0, marginTop: 2 }}
                                    />
                                )}
                            </button>
                            {bestPracticesExpanded ? (
                                <BestPracticeList id={bestPracticesId} isWindows={snapshot.isWindows} />
                            ) : null}
                        </StepRow>
                    </Box>
                </Box>
            </Modal.Body>
            <Modal.Footer
                style={{
                    padding: `${spacing.xl}px ${spacing.xl}px ${spacing.xxl}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Link
                    href={GPU_GUIDANCE_LEARN_MORE_URL}
                    target="_blank"
                    rel="noopener"
                    onClick={() => trackLinkClicked("support-article")}
                    style={{ color: FIGMA_LINK }}
                >
                    {t("gpuGuidance.learnMore")}
                </Link>
                <Button
                    variant="tertiary"
                    onClick={onIgnore}
                    disabled={busy}
                    style={{ color: colors.gray15, fontWeight: 600 }}
                >
                    {t("gpuGuidance.ignoreIssues")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function StepRow({ n, children }: { n: number; children: ReactNode }) {
    return (
        <Box as="li" display="flex" alignItems="flex-start" style={{ gap: 13 }}>
            <StepBadge n={n} />
            <Box style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>{children}</Box>
        </Box>
    );
}

function StepBadge({ n }: { n: number }) {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            aria-hidden="true"
            style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                flexShrink: 0,
                background: colors.gray15,
                color: colors.white,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: "24px",
            }}
        >
            {n}
        </Box>
    );
}

function StepLink({
    href,
    onClick,
    children,
}: {
    href: string;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    children: ReactNode;
}) {
    return (
        <Link href={href} target="_blank" rel="noopener" onClick={onClick} style={{ color: FIGMA_LINK }}>
            {children}
        </Link>
    );
}

function BestPracticeList({ id, isWindows }: { id: string; isWindows: boolean }) {
    const { t } = useTranslation();

    return (
        <Box
            as="ul"
            id={id}
            role="region"
            display="flex"
            flexDirection="column"
            style={{
                margin: `${spacing.lg}px 0 0`,
                padding: `0 0 0 ${spacing.lg}px`,
                listStyle: "none",
                gap: 16,
            }}
        >
            <BestPracticeBullet>
                <BestPracticeLinkedLine
                    isWindows={isWindows}
                    item="hags"
                    enable={t("gpuGuidance.steps.practices.hags.enable")}
                    emphasis={t("gpuGuidance.steps.practices.hags.emphasis")}
                    rest={t("gpuGuidance.steps.practices.hags.in")}
                />
            </BestPracticeBullet>
            <BestPracticeBullet>
                <BestPracticeLinkedLine
                    isWindows={isWindows}
                    item="windowed-games"
                    enable={t("gpuGuidance.steps.practices.windowedGames.enable")}
                    emphasis={t("gpuGuidance.steps.practices.windowedGames.emphasis")}
                    rest={t("gpuGuidance.steps.practices.windowedGames.in")}
                />
            </BestPracticeBullet>
            <BestPracticeBullet>
                <Typography intent="body" color="gray15">
                    {t("gpuGuidance.steps.practices.power")}
                </Typography>
            </BestPracticeBullet>
            <BestPracticeBullet>
                <Typography intent="body" color="gray15">
                    {t("gpuGuidance.steps.practices.tabs")}
                </Typography>
            </BestPracticeBullet>
        </Box>
    );
}

function BestPracticeBullet({ children }: { children: ReactNode }) {
    return (
        <Box as="li" display="flex" alignItems="flex-start" style={{ gap: 10 }}>
            <Box
                aria-hidden="true"
                style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    flexShrink: 0,
                    marginTop: 8,
                    background: colors.gray15,
                }}
            />
            <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Box>
    );
}

function BestPracticeLinkedLine({
    isWindows,
    item,
    enable,
    emphasis,
    rest,
}: {
    isWindows: boolean;
    item: Extract<WizardLinkItem, "hags" | "windowed-games">;
    enable: string;
    emphasis: string;
    rest: string;
}) {
    const { t } = useTranslation();

    return (
        <Typography intent="body" color="gray15">
            {enable}{" "}
            <Typography as="span" intent="body" weight="semibold" color="gray15">
                {emphasis}
            </Typography>{" "}
            {rest}{" "}
            {isWindows ? (
                <StepLink
                    href="#"
                    onClick={(event) => {
                        event.preventDefault();
                        trackLinkClicked(item);
                        openDefaultGraphicsSettings();
                    }}
                >
                    {t("gpuGuidance.steps.gpuPreference.windowsSettings")}
                </StepLink>
            ) : (
                t("gpuGuidance.steps.gpuPreference.windowsSettings")
            )}
        </Typography>
    );
}
